import { useState, useEffect, type ChangeEvent } from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { Theme } from "@mui/material/styles";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setDarkMode,
  setMessage,
} from "context";
import api from "services/api";

function Configurator(): JSX.Element {
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    sidenavColor,

    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [disabled, setDisabled] = useState<boolean>(false);
  const [exibirLojaVirtual, setExibirLojaVirtual] = useState<boolean>(true);
  const [emailNotificacaoOrcamento, setEmailNotificacaoOrcamento] =
    useState<string>("");
  const [savingEmailOrcamento, setSavingEmailOrcamento] =
    useState<boolean>(false);
  const [loadingSiteSettings, setLoadingSiteSettings] = useState<boolean>(true);
  const sidenavColors: (
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
  )[] = ["primary", "dark", "info", "success", "warning", "error"];

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  useEffect(() => {
    let alive = true;
    void api
      .get("/site-settings")
      .then((res) => {
        if (!alive || res.status < 200 || res.status >= 300) return;
        const data = res.data as {
          exibirLojaVirtual?: boolean;
          emailNotificacaoOrcamento?: string | null;
        };
        if (typeof data.exibirLojaVirtual === "boolean") {
          setExibirLojaVirtual(data.exibirLojaVirtual);
        }
        if (data.emailNotificacaoOrcamento !== undefined) {
          setEmailNotificacaoOrcamento(data.emailNotificacaoOrcamento ?? "");
        }
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoadingSiteSettings(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleSalvarEmailNotificacaoOrcamento = async () => {
    const email = emailNotificacaoOrcamento.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Informe um e-mail válido para notificação de orçamento.",
      });
      return;
    }

    setSavingEmailOrcamento(true);
    try {
      const res = await api.put("/site-settings", {
        exibirLojaVirtual,
        emailNotificacaoOrcamento: email || null,
      });
      if (res.status < 200 || res.status >= 300) {
        throw new Error("Resposta inválida");
      }
      const saved = (res.data as { emailNotificacaoOrcamento?: string | null })
        ?.emailNotificacaoOrcamento;
      setEmailNotificacaoOrcamento(saved ?? "");
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "E-mail de notificação de orçamento salvo.",
      });
    } catch {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Não foi possível salvar o e-mail de notificação de orçamento.",
      });
    } finally {
      setSavingEmailOrcamento(false);
    }
  };

  const handleExibirLojaVirtual = async (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const previous = exibirLojaVirtual;
    setExibirLojaVirtual(checked);
    try {
      const res = await api.put("/site-settings", {
        exibirLojaVirtual: checked,
      });
      if (res.status < 200 || res.status >= 300) {
        throw new Error("Resposta inválida");
      }
    } catch {
      setExibirLojaVirtual(previous);
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Não foi possível salvar a exibição da Loja Virtual no site.",
      });
    }
  };

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode
      ? white.main
      : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode
        ? white.main
        : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">Configuração da Página</MDTypography>
          <MDTypography variant="body2" color="text">
            Selecione as Opções.
          </MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        {/*<MDBox>
          <MDTypography variant="h6">Cor do Menu</MDTypography>

          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({
                  borders: { borderWidth },
                  palette: { white, dark, background },
                  transitions,
                }: Theme | any) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: () => {
                    let borderColorValue = sidenavColor === color && dark.main;

                    if (darkMode && sidenavColor === color) {
                      borderColorValue = white.main;
                    }

                    return borderColorValue;
                  },
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                })}
                onClick={() => setSidenavColor(dispatch, color)}
              />
            ))}
          </MDBox>
        </MDBox>*/}

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">Tipo do Menu</MDTypography>
          <MDTypography variant="button" color="text">
            Escolha entre diferentes tipos de Menu Lateral.
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              mt: 2,
              mr: 1,
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              Escuro
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                Transparente
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              Branco
            </MDButton>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox
          mt={3}
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          lineHeight={1}
        >
          <MDBox sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <MDTypography variant="h6">Loja Virtual</MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Ativar a Loja Virtual no site
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              flexShrink: 0,
              width: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Switch
              checked={exibirLojaVirtual}
              onChange={handleExibirLojaVirtual}
              disabled={loadingSiteSettings}
            />
          </MDBox>
        </MDBox>
        <MDBox mt={3}>
          <MDTypography variant="h6">Orçamento pelo site</MDTypography>
          <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0.5, mb: 1.5 }}>
            E-mail que recebe alerta quando um cliente solicita orçamento na landing page.
          </MDTypography>
          <MDInput
            type="email"
            label="E-mail de notificação"
            value={emailNotificacaoOrcamento}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmailNotificacaoOrcamento(e.target.value)
            }
            disabled={loadingSiteSettings || savingEmailOrcamento}
            fullWidth
          />
          <MDBox mt={1.5} display="flex" justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleSalvarEmailNotificacaoOrcamento}
              disabled={loadingSiteSettings || savingEmailOrcamento}
            >
              Salvar e-mail
            </MDButton>
          </MDBox>
        </MDBox>
        {/* <MDBox
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          lineHeight={1}
        >
          <MDTypography variant="h6">Light / Dark</MDTypography>
          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox> */}
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
